import type { ParsedCard, WorldBookEntry, WorldInfoPositionValue } from '../types'
import { WORLD_INFO_POSITION, WORLD_INFO_POSITION_LABELS } from '../types'

const DEFAULT_STORY_STRING =
  '{{#if system}}{{system}}\n{{/if}}' +
  '{{#if wiBefore}}{{wiBefore}}\n{{/if}}' +
  '{{#if description}}{{description}}\n{{/if}}' +
  "{{#if personality}}{{char}}'s personality: {{personality}}\n{{/if}}" +
  '{{#if scenario}}Scenario: {{scenario}}\n{{/if}}' +
  '{{#if wiAfter}}{{wiAfter}}\n{{/if}}'

export function parseCharacterCard(json: any): ParsedCard {
  if (!json || typeof json !== 'object') {
    throw new Error('无效的角色卡数据')
  }

  const isV2OrV3 = json.data && typeof json.data === 'object'
  const data = isV2OrV3 ? json.data : json

  const rawFirstMes = data.first_mes ?? data.firstMes ?? json.first_mes ?? json.firstMes ?? ''
  const firstMes: string =
    Array.isArray(rawFirstMes) ? rawFirstMes.join('\n') : String(rawFirstMes)

  const rawSysPrompt = data.system_prompt ?? json.system_prompt ?? ''

  const rawAltGreetings = data.alternate_greetings ?? json.alternate_greetings
  const alternateGreetings: string[] = Array.isArray(rawAltGreetings) ? rawAltGreetings : []

  const rawGroupGreetings = data.group_only_greetings ?? json.group_only_greetings
  const groupOnlyGreetings: string[] = Array.isArray(rawGroupGreetings) ? rawGroupGreetings : []

  const rawSource = data.source ?? json.source
  const source: string[] = Array.isArray(rawSource) ? rawSource : []

  const rawTags = data.tags ?? json.tags
  const tags: string[] = Array.isArray(rawTags) ? rawTags : []

  const card: ParsedCard = {
    name: data.name ?? json.name ?? '',
    description: data.description ?? json.description ?? '',
    personality: data.personality ?? json.personality ?? '',
    scenario: data.scenario ?? json.scenario ?? '',
    first_mes: firstMes,
    mes_example: data.mes_example ?? data.mesExample ?? json.mes_example ?? json.mesExample ?? '',
    creatorcomment: data.creatorcomment ?? data.creator_comment ?? json.creatorcomment ?? '',
    tags,
    creator: data.creator ?? json.creator ?? '',
    character_version: data.character_version ?? data.characterVersion ?? json.character_version ?? '',
    alternate_greetings: alternateGreetings,
    system_prompt: String(rawSysPrompt),
    post_history_instructions: data.post_history_instructions ?? json.post_history_instructions ?? '',
    character_book: undefined,
    extensions: data.extensions ?? json.extensions ?? {},
    character_uuid: data.character_uuid ?? json.character_uuid ?? '',
    nickname: data.nickname ?? json.nickname ?? '',
    creator_notes: data.creator_notes ?? json.creator_notes ?? '',
    source,
    group_only_greetings: groupOnlyGreetings,
    entries: [],
  }

  const book = data.character_book ?? data.world_book ?? json.character_book ?? json.world_book ?? null
  if (book && typeof book === 'object') {
    card.character_book = {
      name: book.name ?? '',
      description: book.description ?? '',
      scan_depth: book.scan_depth ?? 50,
      token_budget: book.token_budget ?? 500,
      recursive_scanning: book.recursive_scanning ?? false,
      extensions: book.extensions ?? {},
      entries: parseWorldBookEntries(book.entries),
    }
    card.entries = card.character_book.entries
  }

  return card
}

function parseWorldBookEntries(entries: any[]): WorldBookEntry[] {
  if (!Array.isArray(entries)) return []

  return entries.map((entry, index) => {
    const pos = entry.position
    let position: WorldInfoPositionValue = WORLD_INFO_POSITION.BEFORE_CHAR
    if (typeof pos === 'number' && pos >= 0 && pos <= 7) {
      position = pos as WorldInfoPositionValue
    }

    return {
      id: entry.id ?? index,
      keys: Array.isArray(entry.keys) ? entry.keys : [],
      secondary_keys: Array.isArray(entry.secondary_keys) ? entry.secondary_keys : [],
      content: entry.content ?? '',
      comment: entry.comment ?? '',
      name: entry.name ?? '',
      enabled: entry.enabled ?? true,
      disable: entry.disable ?? false,
      constant: entry.constant ?? false,
      selective: entry.selective ?? false,
      selectiveLogic: entry.selectiveLogic ?? 0,
      position,
      order: entry.order ?? entry.insertion_order ?? 100,
      depth: entry.depth ?? 4,
      role: entry.role ?? 0,
      case_sensitive: entry.case_sensitive ?? false,
      matchWholeWords: entry.matchWholeWords ?? null,
      priority: entry.priority ?? 0,
      probability: entry.probability ?? 100,
      useProbability: entry.useProbability ?? true,
      scanDepth: entry.scanDepth ?? null,
      excludeRecursion: entry.excludeRecursion ?? false,
      preventRecursion: entry.preventRecursion ?? false,
      delayUntilRecursion: entry.delayUntilRecursion ?? 0,
      sticky: entry.sticky ?? null,
      cooldown: entry.cooldown ?? null,
      delay: entry.delay ?? null,
      group: entry.group ?? '',
      groupWeight: entry.groupWeight ?? 100,
      ignoreBudget: entry.ignoreBudget ?? false,
      extensions: entry.extensions ?? {},
      insertion_order: entry.insertion_order ?? index,
    }
  })
}

export function replaceVariables(
  text: string,
  charName?: string,
  userName?: string,
): string {
  if (!text) return ''
  const char = charName || 'Character'
  const user = userName || 'User'
  let result = text

  result = result.replace(/<USER>/gi, user)
  result = result.replace(/<BOT>/gi, char)
  result = result.replace(/<CHAR>/gi, char)
  result = result.replace(/<GROUP>/gi, char)

  result = result.replace(/\{\{char\}\}/gi, char)
  result = result.replace(/\{\{user\}\}/gi, user)

  result = stripSTMacros(result)
  return result
}

function stripSTMacros(text: string): string {
  if (!text) return ''
  let result = text

  let prev = ''
  while (prev !== result) {
    prev = result
    result = result.replace(/\{\{#?if\b[^}]*\}\}[\s\S]*?\{\{\/if\}\}/gi, '')
  }
  result = result.replace(/\{\{else\}\}/gi, '')

  result = result.replace(/\{\{[^}]*\}\}/g, '')

  result = result.replace(/\n{3,}/g, '\n\n')
  return result
}

function extractSetVars(text: string): Record<string, string> {
  const vars: Record<string, string> = {}
  if (!text) return vars
  const regex = /\{\{setvar::([^:]+)::([^}]*)\}\}/gi
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    const key = match[1].trim()
    const value = match[2].trim()
    if (key) vars[key] = value
  }
  return vars
}

function extractInitialVariables(
  card: ParsedCard,
  entries: WorldBookEntry[],
): Record<string, string> {
  const sources = [
    card.system_prompt || '',
    card.description || '',
    card.personality || '',
    card.scenario || '',
    card.mes_example || '',
  ]

  const constantEntries = filterActiveEntries(entries).filter((e) => e.constant)
  for (const e of constantEntries) {
    sources.push(e.content)
  }

  const vars: Record<string, string> = {}
  for (const src of sources) {
    Object.assign(vars, extractSetVars(src))
  }
  return vars
}

interface ExtractedConditionalEntry {
  keys: string[]
  content: string
  setVars: Record<string, string>
}

function extractConditionalEntries(entries: WorldBookEntry[]): ExtractedConditionalEntry[] {
  return filterActiveEntries(entries)
    .filter((e) => !e.constant && e.keys.length > 0)
    .sort((a, b) => b.order - a.order)
    .map((e) => {
      const setVars = extractSetVars(e.content)
      const cleanContent = stripSTMacros(e.content)
      return {
        keys: e.keys,
        content: cleanContent,
        setVars,
      }
    })
}

function isBeforePosition(pos: WorldInfoPositionValue): boolean {
  return pos === WORLD_INFO_POSITION.BEFORE_CHAR
    || pos === WORLD_INFO_POSITION.AN_TOP
    || pos === WORLD_INFO_POSITION.EM_TOP
}

function isAfterPosition(pos: WorldInfoPositionValue): boolean {
  return pos === WORLD_INFO_POSITION.AFTER_CHAR
    || pos === WORLD_INFO_POSITION.AN_BOTTOM
    || pos === WORLD_INFO_POSITION.EM_BOTTOM
}

function filterActiveEntries(entries: WorldBookEntry[]): WorldBookEntry[] {
  return entries.filter((e) => e.enabled && !e.disable && e.content)
}

function buildConstantWorldInfo(
  entries: WorldBookEntry[],
): { before: string; after: string } {
  const constantEntries = filterActiveEntries(entries).filter((e) => e.constant)
  const sorted = [...constantEntries].sort((a, b) => b.order - a.order)

  const beforeParts: string[] = []
  const afterParts: string[] = []

  for (const entry of sorted) {
    if (isBeforePosition(entry.position)) {
      beforeParts.push(entry.content)
    } else if (isAfterPosition(entry.position)) {
      afterParts.push(entry.content)
    } else {
      beforeParts.push(entry.content)
    }
  }

  return {
    before: beforeParts.join('\n'),
    after: afterParts.join('\n'),
  }
}

export interface ConditionalEntry {
  keys: string[]
  content: string
  setVars: Record<string, string>
}

export function getConditionalEntries(entries: WorldBookEntry[]): ConditionalEntry[] {
  return extractConditionalEntries(entries)
}

export interface ExportedRegexScript {
  name: string
  find: string
  replace: string
  target: 'display' | 'prompt' | 'both' | 'permanent'
}

const PLACEMENT_LABELS: Record<number, string> = {
  1: '用户输入',
  2: 'AI输出',
  3: '斜杠命令',
  5: '世界书',
  6: '推理块',
}

export function getRegexScripts(card: ParsedCard): ExportedRegexScript[] {
  const raw = card.extensions?.regex_scripts
  if (!Array.isArray(raw)) return []

  return raw
    .filter((s: any) => !s.disabled && s.findRegex)
    .map((s: any) => {
      let target: ExportedRegexScript['target'] = 'permanent'
      if (s.markdownOnly && s.promptOnly) target = 'both'
      else if (s.promptOnly) target = 'prompt'
      else if (s.markdownOnly) target = 'display'

      return {
        name: s.scriptName || '',
        find: s.findRegex || '',
        replace: stripSTMacros(s.replaceString || ''),
        target,
      }
    })
}

export function getRegexScriptDetails(card: ParsedCard): Array<{
  name: string
  find: string
  replace: string
  target: ExportedRegexScript['target']
  placement: string
}> {
  const raw = card.extensions?.regex_scripts
  if (!Array.isArray(raw)) return []

  return raw
    .filter((s: any) => s.findRegex)
    .map((s: any) => {
      let target: ExportedRegexScript['target'] = 'permanent'
      if (s.markdownOnly && s.promptOnly) target = 'both'
      else if (s.promptOnly) target = 'prompt'
      else if (s.markdownOnly) target = 'display'

      const placements: number[] = Array.isArray(s.placement) ? s.placement : []
      const placementStr = placements.map((p: number) => PLACEMENT_LABELS[p] || `未知(${p})`).join(', ') || '全部'

      return {
        name: s.scriptName || '(未命名)',
        find: s.findRegex || '',
        replace: s.replaceString || '',
        target,
        placement: placementStr,
      }
    })
}

function renderStoryString(
  template: string,
  vars: Record<string, string>,
): string {
  let result = template

  for (const [key, value] of Object.entries(vars)) {
    const ifPattern = new RegExp(
      `\\{\\{#if\\s+${key}\\}\\}([\\s\\S]*?)\\{\\{/if\\}\\}`,
      'g',
    )
    result = value
      ? result.replace(ifPattern, '$1')
      : result.replace(ifPattern, '')
  }

  for (const [key, value] of Object.entries(vars)) {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value)
  }

  result = result.replace(/\{\{trim\}\}/g, '')
  result = result.replace(/\n{3,}/g, '\n\n')
  return result.trim()
}

export interface CardOutput {
  systemPrompt: string
  conditionalEntries: ConditionalEntry[]
  regexScripts: ExportedRegexScript[]
  initialVariables: Record<string, string>
  firstMessage: string
  alternateGreetings: string[]
  groupGreetings: string[]
}

export function generateCardOutput(
  card: ParsedCard,
  entries: WorldBookEntry[],
  storyStringTemplate?: string,
): CardOutput {
  const wi = buildConstantWorldInfo(entries)
  const charName = card.name || 'Character'

  const personalityFormatted = card.personality
    ? `${charName}'s personality: ${card.personality}`
    : ''
  const scenarioFormatted = card.scenario
    ? `Scenario: ${card.scenario}`
    : ''

  const template = storyStringTemplate || DEFAULT_STORY_STRING
  const vars: Record<string, string> = {
    system: card.system_prompt || '',
    wiBefore: wi.before,
    description: card.description || '',
    personality: personalityFormatted,
    scenario: scenarioFormatted,
    wiAfter: wi.after,
    persona: '',
    anchorBefore: '',
    anchorAfter: '',
    char: charName,
    user: 'User',
  }

  const storyString = stripSTMacros(renderStoryString(template, vars))

  const sections: string[] = []

  if (storyString) {
    sections.push(storyString)
  }

  if (card.post_history_instructions) {
    sections.push(replaceVariables(card.post_history_instructions, charName))
  }

  if (card.mes_example) {
    sections.push('')
    sections.push(replaceVariables(card.mes_example, charName))
  }

  const allGreetings = [card.first_mes, ...(card.alternate_greetings || [])]
    .map((g) => replaceVariables(g, charName))
    .filter(Boolean)

  const groupGreetings = (card.group_only_greetings || [])
    .map((g) => replaceVariables(g, charName))
    .filter(Boolean)

  const conditionalEntries = getConditionalEntries(entries)
  const regexScripts = getRegexScripts(card)
  const initialVariables = extractInitialVariables(card, entries)

  return {
    systemPrompt: sections.join('\n').trim(),
    conditionalEntries,
    regexScripts,
    initialVariables,
    firstMessage: allGreetings[0] || '',
    alternateGreetings: allGreetings.slice(1),
    groupGreetings,
  }
}

export function generateMarkdown(
  card: ParsedCard,
  entries: WorldBookEntry[],
): string {
  const sections: string[] = []

  if (card.name) {
    sections.push(`# ${replaceVariables(card.name)}`)
    sections.push('')
  }

  if (card.system_prompt) {
    sections.push('## System Prompt')
    sections.push('')
    sections.push(replaceVariables(card.system_prompt))
    sections.push('')
  }

  if (card.description) {
    sections.push('## Description')
    sections.push('')
    sections.push(replaceVariables(card.description))
    sections.push('')
  }

  if (card.personality) {
    sections.push('## Personality')
    sections.push('')
    sections.push(replaceVariables(card.personality))
    sections.push('')
  }

  if (card.scenario) {
    sections.push('## Scenario')
    sections.push('')
    sections.push(replaceVariables(card.scenario))
    sections.push('')
  }

  if (card.first_mes) {
    sections.push('## First Message')
    sections.push('')
    sections.push(replaceVariables(card.first_mes))
    sections.push('')
  }

  if (card.alternate_greetings && card.alternate_greetings.length > 0) {
    const nonEmpty = card.alternate_greetings.filter((g) => g)
    if (nonEmpty.length > 0) {
      sections.push('## Alternate Greetings')
      sections.push('')
      for (let i = 0; i < nonEmpty.length; i++) {
        sections.push(`### Greeting ${i + 1}`)
        sections.push('')
        sections.push(replaceVariables(nonEmpty[i]))
        sections.push('')
      }
    }
  }

  if (card.group_only_greetings && card.group_only_greetings.length > 0) {
    const nonEmpty = card.group_only_greetings.filter((g) => g)
    if (nonEmpty.length > 0) {
      sections.push('## Group Greetings')
      sections.push('')
      for (let i = 0; i < nonEmpty.length; i++) {
        sections.push(`### Greeting ${i + 1}`)
        sections.push('')
        sections.push(replaceVariables(nonEmpty[i]))
        sections.push('')
      }
    }
  }

  if (card.mes_example) {
    sections.push('## Example Dialogue')
    sections.push('')
    sections.push(replaceVariables(card.mes_example))
    sections.push('')
  }

  const activeEntries = entries.filter((e) => e.enabled && !e.disable)
  if (activeEntries.length > 0) {
    sections.push('## World Info')
    sections.push('')

    const grouped = new Map<string, WorldBookEntry[]>()
    for (const entry of activeEntries) {
      const label = WORLD_INFO_POSITION_LABELS[entry.position] ?? `pos_${entry.position}`
      if (!grouped.has(label)) grouped.set(label, [])
      grouped.get(label)!.push(entry)
    }

    for (const [label, groupEntries] of grouped) {
      const sorted = [...groupEntries].sort((a, b) => b.order - a.order)
      for (const entry of sorted) {
        const keyStr = entry.keys.join(', ')
        const title = entry.comment || entry.name || keyStr || `Entry ${entry.id}`
        const tags: string[] = []
        if (entry.constant) tags.push('constant')
        if (entry.selective) tags.push('selective')
        tags.push(`pos: ${label}`)
        sections.push(`### ${title} [${tags.join(', ')}]`)
        sections.push('')
        if (keyStr) {
          sections.push(`**Keywords:** ${keyStr}`)
          sections.push('')
        }
        sections.push(replaceVariables(entry.content))
        sections.push('')
      }
    }
  }

  if (card.post_history_instructions) {
    sections.push('## Post-History Instructions')
    sections.push('')
    sections.push(replaceVariables(card.post_history_instructions))
    sections.push('')
  }

  return sections.join('\n').trim()
}

export function extractStatusBar(regexScripts: any[]): string | null {
  if (!Array.isArray(regexScripts)) return null

  for (const script of regexScripts) {
    if (!script?.scriptName || !script?.replacement) continue

    const name = String(script.scriptName).toLowerCase()
    if (
      name.includes('status') ||
      name.includes('stats') ||
      name.includes('panel')
    ) {
      const replacement = String(script.replacement)
      if (/<[^>]+>/.test(replacement)) {
        return replacement
      }
    }
  }

  return null
}
